import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, LL_V_L, ModuleFE_Thunderstorm, TS_Link, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { FAQType_Child, FAQType_Parent, FAQType_VideoChild } from '@app/advisor-ui-shared';
import { Component_EditorHeader } from '@app/styles';
import { SearchableItem_FAQ } from '../Page_AdvisorUI/consts.js';
import { __stringify, _keys, cloneObj, dbObjectToId, KeysOfDB_Object } from '@nu-art/ts-common';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { DropDown_FAQType, MultiSelect_FAQ } from '../../_entity/frequently-asked-question/ui-components.js';
import { ModuleFE_FAQ } from '../../_entity.js';
import './Editor_FAQ.scss';
const viewMode_View = 'view';
const viewMode_Edit = 'edit';
export class Editor_FAQ extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        state.editMode = nextProps.editMode ?? (!state.editable.item._id ? true : (this.state?.editMode ?? false));
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    getItemActions = () => {
        return [{
                label: 'Edit FAQ',
                action: async () => this.setState({ editMode: true }),
            }, {
                label: 'Delete FAQ',
                action: async () => {
                    await this.state.editable.delete();
                    this.props.deselect?.();
                }
            }, {
                label: 'Export JSON',
                action: async () => {
                    const item = this.state.editable.item;
                    ModuleFE_Thunderstorm.downloadFile({
                        fileName: `FAQ - ${item.label}`,
                        mimeType: 'application/json',
                        content: __stringify(item, true)
                    });
                }
            }];
    };
    getEditModeActions = () => {
        const actions = [];
        //Actions for existing item
        if (this.state.editable.item._id)
            actions.push({
                label: 'Done',
                action: async () => this.setState({ editMode: false }),
            });
        //Actions for new item
        else {
            if (this.props.deselect)
                actions.push({
                    label: 'Cancel',
                    action: async () => {
                        return this.props.deselect();
                    },
                });
            actions.push({
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                },
            });
        }
        return actions;
    };
    onFAQTypeSelected = async (type) => {
        const editable = this.state.editable;
        if (type === editable.item.type)
            return;
        const faq = cloneObj(editable.item);
        faq.type = type;
        const clearKeys = (keysToKeep) => {
            _keys(faq).forEach(key => {
                if (keysToKeep.includes(key))
                    return;
                faq[key] = undefined;
            });
        };
        switch (faq.type) {
            case FAQType_Parent: {
                const keysToKeep = [...KeysOfDB_Object, 'label', 'type'];
                clearKeys(keysToKeep);
                faq.childrenIds = [];
                break;
            }
            case FAQType_Child: {
                const keysToKeep = [...KeysOfDB_Object, 'label', 'type'];
                clearKeys(keysToKeep);
                faq.answer = '';
                break;
            }
            case FAQType_VideoChild: {
                const keysToKeep = [...KeysOfDB_Object, 'label', 'type'];
                clearKeys(keysToKeep);
                faq.videoUrl = '';
                break;
            }
        }
        editable.updateItem(faq);
        return editable.save();
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__faq', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(Component_EditorHeader, { editable: this.state.editable, searchItem: SearchableItem_FAQ, editMode: this.state.editMode, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions() });
    };
    renderBody = () => {
        return _jsxs(LL_V_L, { className: 'editor__faq__body', children: [this.renderEditor_Label(), this.renderEditor_Type(), this.renderEditors_Parent(), this.renderEditors_Child(), this.renderEditors_VideoChild()] });
    };
    // ######################## Render - Editors ########################
    renderEditor_Label = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("p", { className: 'editor__value', children: editable.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label' }) }
                ] }) });
    };
    renderEditor_Type = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Type', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: editable.item.type }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DropDown_FAQType.selectable, { selected: editable.item.type, onSelected: this.onFAQTypeSelected }) }
                ] }) });
    };
    // ######################## Render - Editors - Parent ########################
    renderEditors_Parent = () => {
        if (this.state.editable.item.type !== FAQType_Parent)
            return;
        const editable = this.state.editable;
        return _jsx(_Fragment, { children: this.renderEditor_Parent_Children(editable) });
    };
    renderEditor_Parent_Children = (editable) => {
        const viableFAQIds = ModuleFE_FAQ.cache.filter(faq => faq._id !== editable.item._id && faq.type !== FAQType_Parent).map(dbObjectToId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Children FAQs', children: _jsx(MultiSelect_FAQ, { editable: editable, prop: 'childrenIds', disabled: !this.state.editMode, itemFilter: id => viableFAQIds.includes(id) }) });
    };
    // ######################## Render - Editors - Child ########################
    renderEditors_Child = () => {
        if (this.state.editable.item.type !== FAQType_Child)
            return;
        const editable = this.state.editable;
        return _jsx(_Fragment, { children: this.renderEditors_Child_Answer(editable) });
    };
    renderEditors_Child_Answer = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Answer', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("p", { className: 'editor__value', children: editable.item.answer }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_TextArea, { resizeWithText: true, editable: editable, prop: 'answer' }) }
                ] }) });
    };
    // ######################## Render - Editors - Video Child ########################
    renderEditors_VideoChild = () => {
        if (this.state.editable.item.type !== FAQType_VideoChild)
            return;
        const editable = this.state.editable;
        return _jsxs(_Fragment, { children: [this.renderEditors_VideoChild_UILabel(editable), this.renderEditors_VideoChild_VideoURL(editable)] });
    };
    renderEditors_VideoChild_UILabel = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'UI Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("p", { className: 'editor__value', children: editable.item.uiLabel }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: editable, prop: 'uiLabel' }) }
                ] }) });
    };
    renderEditors_VideoChild_VideoURL = (editable) => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Video URL', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx(TS_Link, { url: editable.item.videoUrl, target: '_blank', children: "Link" }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'videoUrl' }) }
                ] }) });
    };
}
//# sourceMappingURL=Editor_FAQ.js.map